<div mat-dialog-title>
  <h3>
    <ume-flag [code]="leiProfile.attributes.entity.legalAddress.country"></ume-flag>
    {{ leiProfile | leiCompanyName }} ({{ leiProfile.attributes.lei }})
  </h3>
</div>

<mat-dialog-content>
  <mat-tab-group dynamicHeight>
    <mat-tab label="Entity details">
      <div class="entity-content">
        @if (leiProfile.attributes.entity.otherNames) {
          <dl class="entity-row">
            <dt>Other Entity Names:</dt>
            <dd>{{ leiProfile | pipeFunction: getOtherEntityNames }}</dd>
          </dl>
        }

        @if (leiProfile.attributes.entity.transliteratedOtherNames) {
          <dl class="entity-row">
            <dt>Transliterated Other Entity Names:</dt>
            <dd>{{ leiProfile | pipeFunction: getOtherTransliteratedNames }}</dd>
          </dl>
        }

        @if (leiProfile.attributes.entity.registeredAs) {
          <dl class="entity-row">
            <dt>Registered as:</dt>
            <dd>{{ leiProfile.attributes.entity.registeredAs }}</dd>
          </dl>
        }

        @if (leiProfile.attributes.registration.otherValidationAuthorities) {
          <dl class="entity-row">
            <dt>Other Registration Authorities:</dt>
            <dd>
              {{
                leiProfile.attributes.registration.otherValidationAuthorities
                  | pipeFunction: getOtherValidationAuthorities
              }}
            </dd>
          </dl>
        }

        @if (leiProfile.attributes.entity.registeredAt) {
          <dl class="entity-row">
            <dt>Registration Authority ID:</dt>
            <dd>{{ leiProfile.attributes.entity.registeredAt.id }}</dd>
          </dl>
        }

        @if (leiProfile.attributes.entity.jurisdiction) {
          <dl class="entity-row">
            <dt>Legal Jurisdiction:</dt>
            <dd>
              <ume-flag [code]="leiProfile.attributes.entity.jurisdiction"></ume-flag>
              {{
                countryService.getCountryBy('code', leiProfile.attributes.entity.jurisdiction)?.name
              }}
            </dd>
          </dl>
        }

        @if (leiProfile.attributes.entity.category) {
          <dl class="entity-row">
            <dt>Entity Category:</dt>
            <dd>{{ leiProfile.attributes.entity.category }}</dd>
          </dl>
        }

        @if (leiProfile.attributes.entity.legalForm.id) {
          <dl class="entity-row">
            <dt>Entity Legal Form Code:</dt>
            <dd>{{ leiProfile.attributes.entity.legalForm.id }}</dd>
          </dl>
        }

        @if (leiProfile.attributes.entity.legalForm.other) {
          <dl class="entity-row">
            <dt>Other Legal Form:</dt>
            <dd>{{ leiProfile.attributes.entity.legalForm.other }}</dd>
          </dl>
        }

        @if (leiProfile.attributes.entity.associatedEntity) {
          <dl class="entity-row">
            <dt>Associated Entity:</dt>
            <dd>
              {{ leiProfile.attributes.entity.associatedEntity.lei }}
              <br />
              {{ leiProfile.attributes.entity.associatedEntity.name }}
            </dd>
          </dl>
        }

        @if (leiProfile.attributes.entity.status) {
          <dl class="entity-row">
            <dt>Entity Status:</dt>
            <dd>{{ leiProfile.attributes.entity.status }}</dd>
          </dl>
        }

        @if (leiProfile.attributes.entity.expiration.date) {
          <dl class="entity-row">
            <dt>Expiration Date:</dt>
            <dd>{{ leiProfile.attributes.entity.expiration.date | date: 'mediumDate' }}</dd>
          </dl>
        }

        @if (leiProfile.attributes.entity.expiration.reason) {
          <dl class="entity-row">
            <dt>Expiration Reason:</dt>
            <dd>{{ leiProfile.attributes.entity.expiration.reason }}</dd>
          </dl>
        }

        @if (leiProfile.attributes.entity.successorEntity) {
          <dl class="entity-row">
            <dt>Successor Entity:</dt>
            <dd>
              {{ leiProfile.attributes.entity.successorEntity.lei }}
              <br />
              {{ leiProfile.attributes.entity.successorEntity.name }}
            </dd>
          </dl>
        }
      </div>
    </mat-tab>

    <mat-tab label="Addresses">
      <div class="entity-content">
        @if (leiProfile.attributes.entity.legalAddress.country) {
          <dl class="entity-row">
            <dt>Legal address</dt>
            <dd>
              {{ leiProfile.attributes.entity.legalAddress.addressLines[0] }}
              <br />
              {{ leiProfile.attributes.entity.legalAddress.postalCode }},
              {{ leiProfile.attributes.entity.legalAddress.city }}
              <br />
              <ume-flag [code]="leiProfile.attributes.entity.legalAddress.country"></ume-flag>
              {{
                countryService.getCountryBy(
                  'code',
                  leiProfile.attributes.entity.legalAddress.country
                )?.name
              }}
            </dd>
          </dl>
        }

        @if (leiProfile.attributes.entity.headquartersAddress) {
          <dl class="entity-row">
            <dt>Headquarters address</dt>
            <dd>
              {{ leiProfile.attributes.entity.headquartersAddress.addressLines[0] }}
              <br />
              {{ leiProfile.attributes.entity.headquartersAddress.postalCode }},
              {{ leiProfile.attributes.entity.headquartersAddress.city }}
              <br />
              <ume-flag
                [code]="leiProfile.attributes.entity.headquartersAddress.country"></ume-flag>
              {{
                countryService.getCountryBy(
                  'code',
                  leiProfile.attributes.entity.headquartersAddress.country
                )?.name
              }}
            </dd>
          </dl>
        }
      </div>
    </mat-tab>

    <mat-tab label="Registration details">
      <div class="entity-content">
        @if (leiProfile.attributes.registration.initialRegistrationDate) {
          <dl class="entity-row">
            <dt>Initial registration date:</dt>
            <dd>
              {{ leiProfile.attributes.registration.initialRegistrationDate | date: 'mediumDate' }}
            </dd>
          </dl>
        }

        @if (leiProfile.attributes.registration.lastUpdateDate) {
          <dl class="entity-row">
            <dt>Last update date:</dt>
            <dd>{{ leiProfile.attributes.registration.lastUpdateDate | date: 'mediumDate' }}</dd>
          </dl>
        }

        @if (leiProfile.attributes.registration.status) {
          <dl class="entity-row">
            <dt>Registration status:</dt>
            <dd>{{ leiProfile.attributes.registration.status }}</dd>
          </dl>
        }

        @if (leiProfile.attributes.registration.nextRenewalDate) {
          <dl class="entity-row">
            <dt>Next renewal date:</dt>
            <dd>{{ leiProfile.attributes.registration.nextRenewalDate | date: 'mediumDate' }}</dd>
          </dl>
        }

        @if (leiProfile.attributes.registration.managingLou) {
          <dl class="entity-row">
            <dt>Managing LOU:</dt>
            <dd>
              {{ leiProfile.attributes.registration.managingLou }}
            </dd>
          </dl>
        }

        @if (leiProfile.attributes.registration.otherValidationAuthorities) {
          <dl class="entity-row">
            <dt>Other validation authorities:</dt>
            <dd>{{ leiProfile.attributes.registration.otherValidationAuthorities }}</dd>
          </dl>
        }
      </div>
    </mat-tab>
  </mat-tab-group>
</mat-dialog-content>
